import React, { useState, useEffect } from 'react';
import {
  Box, Card, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Dialog, DialogTitle,
  DialogContent, DialogActions, Grid, TextField, CircularProgress,
  Alert, IconButton, Switch, FormControlLabel
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import axiosInstance from '../../api/axiosConfig';

const Courses = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);

  const [formData, setFormData] = useState({
    courseName: '',
    durationMonths: '',
    isActive: true,
  });

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/courses');
      setCourses(response.data);
      setError(null);
    } catch (err) {
      console.error(err);
      setError('Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = async (course = null) => {
    if (course) {
      try {
        const res = await axiosInstance.get(`/courses/${course.courseId}`);
        const data = res.data;

        setEditingCourse(data);
        setFormData({
          courseName: data.courseName || '',
          durationMonths: data.durationMonths || '',
          isActive: data.isActive ?? true,
        });
      } catch (err) {
        alert('Failed to load course details');
        return;
      }
    } else {
      setEditingCourse(null);
      setFormData({
        courseName: '',
        durationMonths: '',
        isActive: true,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCourse(null);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSaveCourse = async () => {
    try {
      if (editingCourse) {
        await axiosInstance.put(`/courses/${editingCourse.courseId}`, {
          courseId: editingCourse.courseId,
          ...formData,
        });
      } else {
        await axiosInstance.post('/courses', formData);
      }
      handleCloseDialog();
      fetchCourses();
    } catch (err) {
      console.error(err);
      alert('Error saving course');
    }
  };

  const handleDeleteCourse = async (id) => {
    if (!window.confirm('Delete this course?')) return;
    try {
      await axiosInstance.delete(`/courses/${id}`);
      fetchCourses();
    } catch (err) {
      alert('Error deleting course');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="h4" fontWeight="bold">Course Management</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
          Add Course
        </Button>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ backgroundColor: '#1976d2' }}>
            <TableRow>
              <TableCell sx={{ color: '#fff' }}>Course Name</TableCell>
              <TableCell sx={{ color: '#fff' }}>Duration (Months)</TableCell>
              <TableCell sx={{ color: '#fff' }}>Status</TableCell>
              <TableCell align="right" sx={{ color: '#fff' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {courses.map(course => (
              <TableRow key={course.courseId}>
                <TableCell>{course.courseName}</TableCell>
                <TableCell>{course.durationMonths}</TableCell>
                <TableCell>
                  {course.isActive ? 'Active' : 'Inactive'}
                </TableCell>
                <TableCell align="right">
                  <IconButton onClick={() => handleOpenDialog(course)} color="primary">
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDeleteCourse(course.courseId)} color="error">
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingCourse ? 'Edit Course' : 'Add Course'}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Course Name"
                name="courseName"
                value={formData.courseName}
                onChange={handleFormChange}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Duration (Months)"
                type="number"
                name="durationMonths"
                value={formData.durationMonths}
                onChange={handleFormChange}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={handleFormChange}
                    name="isActive"
                  />
                }
                label="Active Course"
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveCourse}>
            {editingCourse ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Courses;
